import { useQuery, type QueryKey } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    collapsePurchaseOrderPartsByPartId,
    PurchaseOrdersFeedInput,
    PurchaseOrdersFeedResponse,
} from '../../types/inventory/purchaseOrders';

const FEED_QUERY_CURSOR_INDEX = 6;

const getPurchaseOrdersFeed = async (
    sessionId: string,
    input: PurchaseOrdersFeedInput,
): Promise<PurchaseOrdersFeedResponse> => {
    const { data } = await axios.post<PurchaseOrdersFeedResponse>(
        `${API_URL}/inventory/purchase-orders/feed`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return {
        ...data,
        requests: (data.requests ?? []).map((request) => ({
            ...request,
            invoiceNumber: request.invoiceNumber ?? null,
            fulfillerEmail: request.fulfillerEmail ?? null,
            fulfillerName: request.fulfillerName ?? null,
            vehicle: request.vehicle ?? null,
            parts: collapsePurchaseOrderPartsByPartId(request.parts ?? []),
        })),
    };
};

function isSameFeedIgnoringCursor(a: QueryKey, b: QueryKey): boolean {
    if (a.length !== b.length) return false;
    return a.every((part, i) => i === FEED_QUERY_CURSOR_INDEX || part === b[i]);
}

export const useGetPurchaseOrdersFeed = (
    input: PurchaseOrdersFeedInput = {},
    options?: {
        enabled?: boolean;
        /** Keep showing the current page while the next cursor page loads. */
        keepPreviousPage?: boolean;
    },
) => {
    const sessionContext = useContext(SessionContext);

    // An empty selection would become `$in: []` on the server and match
    // nothing; treat it as 'all' (the server still clamps to the session's
    // authorized locations). Mirrors useInventoryFeed.
    const locationIds =
        Array.isArray(input.locationIds) && input.locationIds.length === 0
            ? 'all'
            : input.locationIds;
    const feedInput: PurchaseOrdersFeedInput = { ...input, locationIds };

    const queryKey: QueryKey = [
        '/inventory/purchase-orders/feed',
        sessionContext?.organizationId ?? '',
        input.statusFilter ?? 'pending',
        // Omitted locationIds means the server scopes to the session's
        // authorized locations, which is not the same result as 'all'.
        locationIds === undefined
            ? 'session'
            : locationIds === 'all'
                ? 'all'
                : locationIds.join(','),
        input.search ?? '',
        input.purchaseOrderId ?? '',
        input.cursor ?? '',
        input.limit ?? 50,
    ];

    return useQuery({
        queryKey,
        queryFn: () => getPurchaseOrdersFeed(sessionContext?.sessionId ?? '', feedInput),
        enabled: (options?.enabled ?? true) && !!sessionContext?.sessionId,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchInterval: 1000 * 60 * 1,
        placeholderData: options?.keepPreviousPage
            ? (previousData, previousQuery) =>
                previousQuery && isSameFeedIgnoringCursor(previousQuery.queryKey, queryKey)
                    ? previousData
                    : undefined
            : undefined,
    });
};
